import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight, Check, TrendingUp, Users, Package, BarChart3, Download } from 'lucide-react';
import api, { API_BASE } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';

const SupplierLanding = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [form, setForm] = useState({ company: '', email: '', phone: '', city: '' });
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get('/public/stats').then(r => setStats(r.data)).catch(() => setStats(null));
  }, []);

  const submit = (e) => {
    e.preventDefault();
    toast({ title: 'Redirection vers l\'inscription', description: 'Finalisez votre compte fournisseur en 1 minute.' });
    navigate('/inscription');
  };

  return (
    <div>
      <nav className="max-w-7xl mx-auto px-4 pt-6 flex items-center gap-1.5 text-sm text-[#7A6F5C]">
        <Link to="/" className="hover:text-[#1F4D3F]">Accueil</Link>
        <ChevronRight className="w-3.5 h-3.5" />
        <span className="text-[#2D2A26]">Devenir fournisseur</span>
      </nav>

      <section className="max-w-7xl mx-auto px-4 py-12 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#C75B27] font-medium mb-2">Espace fournisseurs</div>
          <h1 className="font-serif text-4xl lg:text-5xl font-semibold text-[#1F4D3F] leading-tight">
            Vendez vos produits dentaires sur la première plateforme algérienne
          </h1>
          <p className="mt-4 text-[#5A5247] text-lg">
            Touchez plus de 3 000 cabinets dentaires actifs en Algérie. Listez vos références,
            comparez-vous à la concurrence, et augmentez vos ventes en ligne.
          </p>
          <div className="mt-8 grid grid-cols-2 gap-4">
            {[
              { icon: Users,       label: stats?.dentists  != null ? `${stats.dentists}+`  : '—', sub: 'dentistes inscrits' },
              { icon: Package,     label: stats?.products  != null ? `${stats.products}+`  : '—', sub: 'produits au catalogue' },
              { icon: BarChart3,   label: stats?.suppliers != null ? `${stats.suppliers}+` : '—', sub: 'fournisseurs partenaires' },
              { icon: TrendingUp,  label: stats?.orders    != null ? `${stats.orders}+`    : '—', sub: 'commandes traitées' },
            ].map((s) => (
              <div key={s.sub} className="bg-white border border-[#E8E0D0] rounded-xl p-4" data-testid={`landing-stat-${s.sub.replace(/\s+/g, '-')}`}>
                <s.icon className="w-5 h-5 text-[#1F4D3F] mb-2" />
                <div className="font-serif text-2xl font-semibold text-[#1F4D3F]">{s.label}</div>
                <div className="text-xs text-[#7A6F5C]">{s.sub}</div>
              </div>
            ))}
          </div>

          {/* CSV template download — pre-onboarding */}
          <div className="mt-6 bg-[#FAF7F2] border border-[#E8E0D0] rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Download className="w-5 h-5 text-[#1F4D3F] flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-medium text-[#2D2A26] text-sm">Vous avez déjà votre catalogue ?</div>
                <p className="text-xs text-[#7A6F5C] mt-1">
                  Téléchargez notre modèle CSV et envoyez-nous votre catalogue rempli — nous l'importerons en bloc.
                </p>
                <a href={`${API_BASE}/public/supplier-csv-template`} download
                  data-testid="landing-csv-template-download"
                  className="inline-flex items-center gap-1.5 mt-2 text-xs text-[#1F4D3F] font-medium hover:underline">
                  <Download className="w-3 h-3" /> Modèle CSV (5 colonnes) →
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border border-[#E8E0D0] rounded-2xl p-8 shadow-sm">
          <h2 className="font-serif text-2xl font-semibold text-[#1F4D3F]">Démarrer maintenant</h2>
          <p className="text-sm text-[#7A6F5C] mt-1">Inscription instantanée — accès immédiat au tableau de bord, sans validation préalable.</p>

          <form onSubmit={submit} className="space-y-4 mt-6">
            <div>
              <label className="text-sm text-[#2D2A26] mb-1.5 block">Société</label>
              <Input value={form.company} onChange={(e) => setForm({...form, company: e.target.value})} placeholder="Nom de votre entreprise" required />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-[#2D2A26] mb-1.5 block">Email</label>
                <Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} placeholder="vous@societe.dz" required />
              </div>
              <div>
                <label className="text-sm text-[#2D2A26] mb-1.5 block">Téléphone</label>
                <Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} placeholder="+213 ..." required />
              </div>
            </div>
            <div>
              <label className="text-sm text-[#2D2A26] mb-1.5 block">Wilaya</label>
              <Input value={form.city} onChange={(e) => setForm({...form, city: e.target.value})} placeholder="Alger, Oran, Constantine..." required />
            </div>
            <Button type="submit" className="w-full h-11 bg-[#1F4D3F] hover:bg-[#173B30] text-white">
              Continuer l'inscription
            </Button>
            <ul className="text-sm text-[#5A5247] space-y-2 pt-2">
              {['Catalogue illimité', 'Dashboard de gestion en temps réel', '100 % gratuit — aucune commission'].map((b) => (
                <li key={b} className="flex items-center gap-2"><Check className="w-4 h-4 text-[#1F4D3F]" /> {b}</li>
              ))}
            </ul>
          </form>
        </div>
      </section>
    </div>
  );
};

export default SupplierLanding;
